import { useCallback, useEffect, useRef, useState } from 'react';
import type { AiAssistantPreviewDocument } from '@asset-metrix/portal-ui';

import { fetchAiAssistantDocumentPreviewUrl } from '../../api/portalGateway/aiAssistantDocument';

/**
 * Resolves cited `documentId`s to object URLs and keeps one previewer per file.
 * A later click on another citation of the same document only replaces
 * pages / provenance.
 */
export function useDocumentPreview() {
  const urlByDocumentId = useRef(new Map<string, string>());
  const [openDocument, setOpenDocument] =
    useState<AiAssistantPreviewDocument | null>(null);

  useEffect(() => {
    const cache = urlByDocumentId.current;
    return () => {
      for (const url of cache.values()) {
        URL.revokeObjectURL(url);
      }
      cache.clear();
    };
  }, []);

  const openDocumentPreview = useCallback(
    async (document: AiAssistantPreviewDocument) => {
      if (document.file) {
        setOpenDocument(document);
        return;
      }

      const documentId = document.documentId;
      if (documentId == null) {
        return;
      }

      const cached = urlByDocumentId.current.get(documentId);
      if (cached) {
        setOpenDocument({ ...document, file: cached });
        return;
      }

      const file = await fetchAiAssistantDocumentPreviewUrl(documentId);
      urlByDocumentId.current.set(documentId, file);
      setOpenDocument({ ...document, file });
    },
    []
  );

  const closeDocumentPreview = useCallback(() => {
    setOpenDocument(null);
  }, []);

  return {
    closeDocumentPreview,
    openDocument,
    openDocumentPreview,
  };
}
